// IMPORT REACT
import React, {
  useContext,
  useEffect,
  useMemo,
  useRef
} from 'react'

// IMPORT UI COMPONENTS
import {
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Select,
  Tooltip,
  Typography,
} from '@mui/material'

import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutline";
import PrintIcon from "@mui/icons-material/Print";
import { printHtmlContent } from "../../../shared/iosPwaPrint";
import html2pdf from "html2pdf.js";
import DownloadIcon from "@mui/icons-material/Download";
import ReportCertificateHeader from './ReportCertificateHeader';
import ReportCertificateFooter from './ReportCertificateFooter';
import { useLocation } from 'react-router-dom';
import {
  GetPatientReportCertificatePreview,
  GetReportCertificateTokenDetail
} from '../../../services/TokenService';
import { PreferencesContext } from '../../../PreferenceContext';
import { AlertType } from '../../../services/models/AlertType';

interface BranchDetails {
  opdId: number;
  consumerId: number;
  organizationId: number;
  branchId: number;
  patientId: number;
  description: string;
}

const CertificateReport = () => {
  const initialBranchDetails: BranchDetails = {
    opdId: 0,
    consumerId: 0,
    organizationId: 0,
    branchId: 0,
    patientId: 0,
    description: "",
  };

  const htmlprintRef = useRef();

  // STATES -- FOR ERROR HANDLING AND LOADING
  const [isLoading, setIsLoading] = React.useState<boolean>(true);

  const [open, setOpen] = React.useState(false);
  const [zoomLevel, setZoomLevel] = React.useState(1);
  const [detailReport, setDetailReport] = React.useState(null);
  const [branchDetails, setBranchDetails] = React.useState<BranchDetails>(initialBranchDetails);
  const { userContext, updateUserContext } = useContext(PreferencesContext);

  const zoomOptions = [0.25, 0.5, 0.75, 1, 1.25, 1.5, 1.75, 2];

  // Getting Seach Paramms for Pagination
  function useQuery() {
    const { search } = useLocation();
    return useMemo(() => new URLSearchParams(search), [search]);
  }

  // GETTTING PARAMS
  let urlParams = useQuery();
  let token = urlParams.get("t");
  let currentToken: string = '';

  if (token !== null && token !== undefined) {
    currentToken = token;
  }

  //CLOSE FOR DIALOG
  const handleClose = (event: any, reason: string) => {
    if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
      return;
    }
    setOpen(false);
  };

  const handleZoomChange = (event: any) => {
    setZoomLevel(parseFloat(event.target.value));
  };

  const handleZoomIn = () => {
    setZoomLevel((prev) => {
      const nextZoom = zoomOptions.find((z) => z > prev) || prev;
      return nextZoom;
    });
  };

  const handleZoomOut = () => {
    setZoomLevel((prev) => {
      const nextZoom =
        [...zoomOptions].reverse().find((z) => z < prev) || prev;
      return nextZoom;
    });
  };

  const handleReportPrint = () => {
    handlePrint();
  }

  const handlePrint = () => {
    if (htmlprintRef.current) {
      printHtmlContent(htmlprintRef.current, {
        printable: htmlprintRef.current.innerHTML,
        type: "raw-html",
        maxWidth: 1000,
        style: ` 
            @media print {
              body, html {
                margin: 0;
                padding: 0;
                background-color: #fff;
                -webkit-print-color-adjust: exact;
                print-color-adjust: exact;
                font-family: Arial, sans-serif !important; /* Set font family */
            }
            @page {
              size: auto;
              margin: 0.50in 0.80in;
            }
            .print-conatiner{
              display: flex;
              flex-direction: column;
              min-height:100vh;
              position:relative;
              margin: 0;
              padding: 0;
              box-sizing: border-box;
            }
            .address-container {
              display: flex;
              justify-content: center;
              align-items: center;
              text-align: center;
              font-size: 12.5px;
            }
            .content {
              flex-grow:1;
            }
            .page-break {
              page-break-before: always;
              top: 100px;                      
            }
            .footer {
              bottom: 0;
              left: 0;
              width: 100%;
            }
            .footer::after{
              content:'',
              display: block;
              height: 50px;
            }
            * {
              font-family: Arial, sans-serif !important; /* Ensure all elements use Arial */
              margin: 0;
              padding: 0;
              box-sizing: border-box;
            }
            .tiptap-editor-preview,
              .tiptap-editor-preview p,
              .tiptap-editor-preview li,
              .tiptap-editor-preview div,
              .tiptap-editor-preview span {
                line-height: 1.4 !important;
            }
            .tiptap-editor-preview p {
              margin-bottom: 4px !important;
            }
            .tiptap-editor-preview ul,
            .tiptap-editor-preview ol {
              padding-left: 16px !important;
              list-style-position: outside !important;
            }
            .tiptap-editor-preview ul {
              list-style-type: disc !important;
            }
            .tiptap-editor-preview ol {
              list-style-type: decimal !important;
            }
            .tiptap-editor-preview li {
              display: list-item !important;
            }
            .tiptap-editor-preview li::marker {
              color: inherit !important;
            }
            /* Ensure TipTap tables keep borders/styles in print */
            .tiptap-editor-preview table {
              border-collapse: collapse !important;
              table-layout: fixed !important;
              width: 100% !important;
              margin: 0 !important;
              overflow: visible !important;
            }
            .tiptap-editor-preview td,
            .tiptap-editor-preview th {
              min-width: 1em !important;
              border: 2px solid #ced4da !important;
              padding: 3px 5px !important;
              vertical-align: top !important;
              box-sizing: border-box !important;
              position: relative !important;
            }
            .tiptap-editor-preview th {
              font-weight: bold !important;
              text-align: left !important;
              background-color: #f1f3f4 !important;
            }
          }
        `,
        scanStyles: true,
        targetStyles: ["*"],
      });
    }
  }

  const handleReportDownload = () => {
    const element = htmlprintRef.current;

    html2pdf()
      .set({
        margin: [12.7, 20.32, 12.7, 20.32],
        image: { type: "jpeg", quality: 1 },
        html2canvas: {
          scale: 10,
          scrollY: 0,
          width: 794,
          minHeight: 1123,
        },
        filename: `report.pdf`,
        jsPDF: { unit: "mm", format: "a4", orientation: "portrait" },
        pagebreak: {
          mode: ["avoid-all", "css", "legacy"],
        },
      })
      .from(element)
      .save();
  }

  const getReportCertificateTokenDetail = async () => {
    try {
      let tokenData: any = null;
      const params = new URLSearchParams();
      params.append("token", token || "");

      const result: any = await GetReportCertificateTokenDetail(params, currentToken);

      if (result?.data?.data) {
        const data = result.data.data;

        tokenData = {
          opdId: data.opdId ?? 0,
          consumerId: data.consumerId ?? 0,
          organizationId: data.organizationId ?? 0,
          branchId: data.branchId ?? 0,
          patientId: data.patientId ?? 0,
          description: data.description ?? 0,
        };

        setBranchDetails(tokenData);
      }
      return tokenData;
    } catch (e) {
      console.error("Error fetching branch details:", e);
    }
  };

  // HANDLE PRINT REPORT CERTIFICATE 
  const fetchReportCertificateDetail = async (
    consumerId: string,
    organizationId: string,
    branchId: string,
    opdId: string,
    patientId: string,
    description: string,
  ) => {
    try {
      const formData = {
        consumerId: consumerId,
        organizationId: organizationId,
        branchId: branchId,
        opdId: opdId,
        patientId: patientId,
        description: description
      };
      const res: any = await GetPatientReportCertificatePreview(formData, currentToken);
      if (res?.data) {
        const Result = {
          previewDescription: res?.data?.previewDescription,
          medicalReportCertificateHeader: res?.data?.medicalReportCertificateHeader,
          patientId: res?.data?.patientId,
          opdId: res?.data?.opdId,
          description: res?.data?.description,
        };
        setDetailReport(Result);
        setOpen(true);
        handleReportPrint();
      }
    } catch (e: any) {
      updateUserContext({
        ...userContext,
        isAlert: true,
        alertMessage: typeof e === "string" ? (e as string) : (e?.message as string),
        alertType: AlertType.Error,
      });
    }
  };

  // FETCH DATA
  const fetchData = async () => {
    try {
      const tokenData = await getReportCertificateTokenDetail();
      if (tokenData?.opdId || 0) {
        await fetchReportCertificateDetail(
          tokenData.consumerId || 0,
          tokenData.organizationId || 0,
          tokenData.branchId || 0,
          tokenData.opdId || 0,
          tokenData.patientId || 0,
          tokenData.description || ""
        );
      }
      setIsLoading(false);
    } catch (e) {
      setIsLoading(false);
    }
  };

  // USE EFFECTS
  useEffect(() => {
    setIsLoading(true);
    fetchData();
  }, []);

  return (
    <Dialog
      fullScreen 
      open={open}
      onClose={handleClose}
      scroll="paper"
      PaperProps={{
        sx: {
          borderRadius: "4px",
          height: "100vh",
          padding: "0px",
          margin: "0px",
          overflow: "hidden",
        },
      }}
    >
      <DialogTitle
        sx={{
          borderBottom: "1px solid #ddd",
          padding: "4px 16px",
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Box
            display="flex"
            alignItems="center"
            gap={0}
            sx={{ padding: 0, margin: 0 }}
          >
            <Select
              value={zoomLevel}
              onChange={handleZoomChange}
              sx={{
                mx: 1,
                minWidth: 70,
                border: "none",
                "& .MuiOutlinedInput-notchedOutline": { border: "none" },
                "&:hover .MuiOutlinedInput-notchedOutline": {
                  border: "none",
                },
                "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                  border: "none",
                },
                backgroundColor: "transparent",
                padding: 0,
              }}
              MenuProps={{
                PaperProps: {
                  sx: {
                    border: "1px solid #c7c5c5",
                    borderRadius: "4px",
                  },
                },
              }}
            >
              {zoomOptions.map((option) => (
                <MenuItem key={option} value={option}>
                  <Typography
                    sx={{
                      fontSize: "13px !important",
                    }}
                  >
                    {(option * 100).toFixed(0)}%
                  </Typography>
                </MenuItem>
              ))}
            </Select>

            <Box display="flex" gap={1}>
              <Tooltip title="Zoom Out" placement="top">
                <IconButton
                  onClick={handleZoomOut}
                  disabled={zoomLevel <= 0.25}
                  sx={{ padding: 0 }}
                >
                  <RemoveCircleOutlineIcon />
                </IconButton>
              </Tooltip>

              <Tooltip title="Zoom In" placement="top">
                <IconButton
                  onClick={handleZoomIn}
                  disabled={zoomLevel >= 2}
                  sx={{ padding: 0 }}
                >
                  <AddCircleOutlineIcon />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>

          <Box
            display="flex"
            justifyContent="flex-start"
            alignItems="center"
          >
            <Tooltip title="Print PDF" placement="top">
              <IconButton aria-label="Print PDF" onClick={handleReportPrint}>
                <PrintIcon />
              </IconButton>
            </Tooltip>

            <Tooltip title="Download PDF" placement="top">
              <IconButton
                aria-label="Download PDF"
                onClick={handleReportDownload}
              >
                <DownloadIcon />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
      </DialogTitle>
      <DialogContent
        sx={{
          height: "100%",
          padding: "16px !important",
          fontFamily: "Arial, sans-serif",
          backgroundColor: "#f1f3f5",
          boxSizing: "border-box",
          overflow: "auto",
        }}
      >
        <Box
          sx={{
            width: "max-content",
            border: "1px solid #ddd",
            boxShadow: "4px rgba(0, 0, 0, 0.2)",
            borderRadius: "2px",
            padding: "30px 50px",
            fontFamily: "Arial, sans-serif",
            backgroundColor: "var(--opd-panel-bg)",
            boxSizing: "border-box",
            transform: `scale(${zoomLevel})`,
            transformOrigin: "center",
            transition: "transform 0.2s ease-in-out",
            margin: "0 auto",
          }}
        >
          <Box ref={htmlprintRef} sx={{ width: "794px" }}>
            <ReportCertificateHeader
              reportData={detailReport}
              selectedSections={{ HEADER: true }}
            />
            {detailReport?.previewDescription && (
              <Box
                className="tiptap-editor-preview"
                sx={{
                  margin: '20px 16px',
                  lineHeight: 1.5,
                  "& p, & li, & td, & th, & div, & span": {
                    lineHeight: "1.5 !important",
                  },
                  "& table": {
                    borderCollapse: "collapse",
                    tableLayout: "fixed",
                    width: "100% !important",
                    minWidth: "100% !important",
                    maxWidth: "100% !important",
                    margin: "0 auto",
                  }
                }}
                dangerouslySetInnerHTML={{ __html: detailReport.previewDescription }}
              />
            )}
            <ReportCertificateFooter
              reportData={detailReport}
              selectedSections={{ FOOTER: true }}
            />
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  )
}

export default CertificateReport
